import {
  ATTACHMENT_MAX_BYTES_DEFAULT,
  ATTACHMENTS_PER_MESSAGE,
  SKIN_TONES,
  type AckResult,
  type Attachment,
  type AttachmentLink,
  type AttachmentUpload,
  type ChatHistory,
  type ChatMessage,
  type PeerId,
  type Reaction,
  type SkinTone,
} from '@live-stream/shared'
import type { Room } from '../room.js'
import type { AppSocket } from '../socket.js'
import { readKey, writeKey, type KeyValueStore } from '../storage.js'
import { derived, patch, readonly, watch, writable, type ReadableStore } from '../store.js'
import { formatBytes } from '../format.js'
import { putWithProgress } from './attachments.js'
import { animatedEmojiUrl, preloadReactions } from './emoji.js'

/** A confirmed line from the server, or one of ours still on its way. */
export interface ChatLine extends ChatMessage {
  pending?: boolean
  /** The server refused it, or the send was lost. The composer offers a retry. */
  failed?: string
}

/**
 * One emoji on its way up the stage. Everything that varies between two
 * reactions is decided here, once, when it arrives — a component only
 * turns these into CSS.
 */
export interface FloatingReaction {
  id: number
  peerId: PeerId
  emoji: Reaction
  /** Which of the lanes at the stage's left edge it rises in. */
  lane: number
  /** Percent of the stage width, added to the lane, so a lane is not a rail. */
  jitter: number
  /** Half-width of the side-to-side drift, px. */
  sway: number
  /** Half-angle of the tilt that goes with the drift, degrees. */
  spin: number
  /** Total flight, ms. */
  duration: number
}

/** The sender's latest reaction, shown on their tile for a moment. */
export interface TileReaction {
  id: number
  emoji: Reaction
}

/**
 * A file on its way into the composer. Begins as a local record, gets the
 * server's id once the upload has an address, and ends ready — with the
 * server's verdict on what it is — or failed with a reason. Ready ones go
 * with the next send.
 */
export interface Upload {
  /** Ours, for the list; stable across the whole life of the upload. */
  localId: string
  /** The server's, once begun. */
  id: string | null
  name: string
  size: number
  status: 'starting' | 'uploading' | 'finishing' | 'ready' | 'failed'
  /** 0..1 while uploading. */
  progress: number
  error?: string
  attachment?: Attachment
  /** A local object URL for a picture, so the composer can show it before anyone has a link. */
  preview?: string
  abort?: AbortController
  /** Kept so a failed upload can be tried again. */
  file?: File
}

export interface ChatState {
  /** Confirmed lines, ascending by seq. */
  messages: ChatLine[]
  /** Our sends awaiting their echo, in send order. */
  pending: ChatLine[]
  sessionId: string | null
  hasOlder: boolean
  loading: boolean
  /** The panel is showing. */
  open: boolean
  /** The list is scrolled to its end; the panel keeps it there as lines arrive. */
  atBottom: boolean
  /** Lines from others that arrived while the panel was closed or scrolled up. */
  unread: number
  /** Of those, the ones that @-mention us or were sent to us privately. */
  unreadMentions: number
  /** Who the composer is writing to: a participant for a private line, null for the room. */
  dmTarget: PeerId | null
  /** Who is typing, by peer id, and when their signal expires. */
  typing: Record<PeerId, number>
  reactions: FloatingReaction[]
  /** Each participant's latest reaction while its badge is up. */
  tileReactions: Record<PeerId, TileReaction>
  /** The skin tone the hands take; remembered per browser. */
  skinTone: SkinTone
  /** Files in the composer, in the order they were added. */
  uploads: Upload[]
  /** Local previews of our own pictures, by attachment id, until the echo has been drawn. */
  previews: Record<string, string>
}

/** How long "X is typing…" stays without a fresh signal. */
const TYPING_TTL_MS = 5_000
/** How often we tell the room we are typing while we keep typing. */
const TYPING_SIGNAL_MS = 3_000
/** How long the reaction badge stays on the sender's tile. */
const TILE_REACTION_MS = 3_000
/** Lanes at the left edge of the stage, the way Meet does it. */
const REACTION_LANES = 5
const PAGE = 50
const SKIN_TONE_KEY = 'ls_skin_tone'
/** A download link this close to expiry is refreshed before it is followed. */
const LINK_MARGIN_MS = 60_000

function newClientMsgId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

const offline = <T>(): AckResult<T> => ({
  ok: false,
  error: { code: 'offline', message: 'Not connected.' },
})

/**
 * In-meeting chat and reactions.
 *
 * Server-ordered: every confirmed line carries a `seq` that is monotonic
 * within the meeting session, and lines are held sorted by it. Our own
 * sends render at once, marked pending, and are matched to their echo by
 * clientMsgId. After a reconnect the chat asks for everything after the
 * highest seq it holds — exactly the gap, no duplicates, no holes — and
 * retries any send that never got its echo; the server treats clientMsgId as
 * idempotent, so a retry never doubles a line.
 */
export class Chat {
  private readonly store: ReturnType<typeof writable<ChatState>>
  readonly state: ReadableStore<ChatState>
  /** Every line to draw: the confirmed ones, then ours on their way. */
  readonly lines: ReadableStore<ChatLine[]>
  /** The highest confirmed seq held. */
  readonly lastSeq: ReadableStore<number>
  /** Display names of the people typing right now, ourselves excluded. */
  readonly typingNames: ReadableStore<string[]>
  /** Participants may write while the switch is on; moderators always. A personal silence beats both. */
  readonly canWrite: ReadableStore<boolean>
  readonly readyUploads: ReadableStore<Upload[]>
  /** Something is still on its way up; the send waits for it. */
  readonly uploading: ReadableStore<boolean>

  /** Who our last typing signal was addressed to, so "stopped" goes to the same place. */
  private typingTo: PeerId | null = null
  private readonly tileTimers = new Map<PeerId, ReturnType<typeof setTimeout>>()
  /** Download links by attachment id, each good until its expiry. */
  private readonly links = new Map<string, AttachmentLink>()
  private readonly linkRequests = new Map<string, Promise<string | null>>()
  private reactionSeq = 0
  private nextLane = 0
  private typingSweep: ReturnType<typeof setInterval> | null = null
  private lastTypingSentAt = 0
  private typingAnnounced = false
  private wired: AppSocket | null = null

  constructor(
    private readonly room: Room,
    private readonly local: KeyValueStore | null,
    private readonly emojiBase: string,
  ) {
    const stored = readKey(local, SKIN_TONE_KEY) ?? ''
    this.store = writable<ChatState>({
      messages: [],
      pending: [],
      sessionId: null,
      hasOlder: false,
      loading: false,
      open: false,
      atBottom: true,
      unread: 0,
      unreadMentions: 0,
      dmTarget: null,
      typing: {},
      reactions: [],
      tileReactions: {},
      skinTone: (SKIN_TONES as readonly string[]).includes(stored) ? (stored as SkinTone) : '',
      uploads: [],
      previews: {},
    })
    this.state = readonly(this.store)

    const messages = derived([this.store], (s) => s.messages)
    const pending = derived([this.store], (s) => s.pending)
    const uploads = derived([this.store], (s) => s.uploads)
    this.lines = derived([messages, pending], (m, p) => [...m, ...p])
    this.lastSeq = derived([messages], (m) => {
      let max = 0
      for (const line of m) if (line.seq > max) max = line.seq
      return max
    })
    const typing = derived([this.store], (s) => s.typing)
    let lastNames: string[] = []
    this.typingNames = derived([typing, room.state], (t, r) => {
      const names = Object.keys(t)
        .filter((id) => id !== r.me)
        .map((id) => r.room.participants[id]?.displayName)
        .filter((name): name is string => Boolean(name))
      // The same names, the same array: nothing to redraw.
      if (names.length === lastNames.length && names.every((n, i) => n === lastNames[i])) {
        return lastNames
      }
      lastNames = names
      return names
    })
    this.canWrite = derived([room.state], (r) => {
      const mine = r.me ? r.room.participants[r.me] : undefined
      const moderator = r.myRole === 'host' || r.myRole === 'cohost'
      return !mine?.chatMuted && (r.room.chatEnabled || moderator)
    })
    this.readyUploads = derived([uploads], (u) => u.filter((x) => x.status === 'ready'))
    this.uploading = derived([uploads], (u) =>
      u.some((x) => x.status !== 'ready' && x.status !== 'failed'),
    )

    watch(
      room.socket,
      (s) => s,
      (sock) => sock && this.wire(sock),
      { immediate: true },
    )
    watch(
      room.state,
      (s) => `${s.phase}|${s.rejoinCount}`,
      () => {
        const { phase } = room.state.get()
        if (phase === 'joined') {
          void this.sync()
          // Other people's reactions arrive unannounced; have the pictures
          // ready so the first one is a burst and not a glyph. After the call
          // has settled, so it never competes with the media negotiation.
          setTimeout(() => {
            if (room.state.get().phase === 'joined') {
              this.preloadReactions()
            }
          }, 3000)
        }
        if (phase === 'idle') this.reset()
      },
      { immediate: true },
    )
    // A private conversation with someone who has left goes back to the room.
    watch(
      room.state,
      (s) => Object.keys(s.room.participants).sort().join(','),
      () => {
        const target = this.s.dmTarget
        if (target && !room.state.get().room.participants[target]) {
          patch(this.store, { dmTarget: null })
        }
      },
    )
  }

  private get s(): ChatState {
    return this.store.get()
  }

  private get me(): PeerId | null {
    return this.room.state.get().me
  }

  private get socket(): AppSocket | null {
    return this.room.socket.get()
  }

  // ------------------------------------------------------------- receiving

  private upsert(message: ChatMessage): boolean {
    const { sessionId } = this.s
    if (sessionId && message.sessionId !== sessionId) return false
    if (!sessionId) patch(this.store, { sessionId: message.sessionId })

    // Our own echo replaces the optimistic line.
    if (message.clientMsgId && message.authorPeerId === this.me) {
      const pending = this.s.pending.filter((p) => p.clientMsgId !== message.clientMsgId)
      if (pending.length !== this.s.pending.length) patch(this.store, { pending })
      // The real link takes over from the local preview once it has had time to load.
      for (const a of message.attachments) {
        if (this.s.previews[a.id]) setTimeout(() => this.dropPreview(a.id), 30_000)
      }
    }

    const list = this.s.messages
    if (list.some((m) => m.id === message.id || m.seq === message.seq)) return false
    // Usually the newest; walk back only as far as needed.
    let i = list.length
    while (i > 0 && (list[i - 1] as ChatLine).seq > message.seq) i--
    patch(this.store, { messages: [...list.slice(0, i), message, ...list.slice(i)] })
    return true
  }

  /** A line from someone else that we are not looking at right now. */
  private countUnread(message: ChatMessage): void {
    const me = this.me
    if (message.authorPeerId === me || (this.s.open && this.s.atBottom)) return
    const mention = message.toPeerId === me || (me !== null && message.mentions.includes(me))
    patch(this.store, {
      unread: this.s.unread + 1,
      unreadMentions: this.s.unreadMentions + (mention ? 1 : 0),
    })
  }

  private readonly onMessage = (message: ChatMessage): void => {
    if (!this.upsert(message)) return
    this.countUnread(message)
  }

  private readonly onCleared = (msg: { sessionId: string }): void => {
    if (this.s.sessionId && msg.sessionId !== this.s.sessionId) return
    patch(this.store, { messages: [], unread: 0, unreadMentions: 0 })
  }

  private readonly onTyping = (msg: { peerId: PeerId; typing: boolean }): void => {
    if (msg.peerId === this.me) return
    const next = { ...this.s.typing }
    if (msg.typing) next[msg.peerId] = Date.now() + TYPING_TTL_MS
    else delete next[msg.peerId]
    patch(this.store, { typing: next })
    this.ensureSweep()
  }

  /**
   * Put a reaction on the stage and on the sender's tile. Lanes are dealt in
   * turn, so two reactions in the same instant rise side by side rather than
   * on top of each other; everything else about the flight is a little
   * different each time, which is what makes a crowd of them look alive.
   */
  private show(peerId: PeerId, emoji: Reaction): void {
    const id = ++this.reactionSeq
    const duration = 3800 + Math.round(Math.random() * 800)
    patch(this.store, {
      reactions: [
        ...this.s.reactions,
        {
          id,
          peerId,
          emoji,
          lane: this.nextLane++ % REACTION_LANES,
          jitter: Math.random() * 4 - 2,
          sway: 10 + Math.random() * 8,
          spin: 4 + Math.random() * 6,
          duration,
        },
      ],
    })
    setTimeout(() => {
      patch(this.store, { reactions: this.s.reactions.filter((r) => r.id !== id) })
    }, duration + 100)

    patch(this.store, { tileReactions: { ...this.s.tileReactions, [peerId]: { id, emoji } } })
    const previous = this.tileTimers.get(peerId)
    if (previous) clearTimeout(previous)
    this.tileTimers.set(
      peerId,
      setTimeout(() => {
        this.tileTimers.delete(peerId)
        if (this.s.tileReactions[peerId]?.id !== id) return
        const next = { ...this.s.tileReactions }
        delete next[peerId]
        patch(this.store, { tileReactions: next })
      }, TILE_REACTION_MS),
    )
  }

  private readonly onReaction = (msg: { peerId: PeerId; emoji: Reaction; at: number }): void => {
    // Ours went up the moment we tapped; the echo is for everyone else.
    if (msg.peerId === this.me) return
    this.show(msg.peerId, msg.emoji)
  }

  /** Signals that were never followed by "stopped" expire on their own. */
  private ensureSweep(): void {
    if (this.typingSweep) return
    this.typingSweep = setInterval(() => {
      const now = Date.now()
      const next: Record<PeerId, number> = {}
      let any = false
      for (const [id, until] of Object.entries(this.s.typing)) {
        if (until > now) {
          next[id] = until
          any = true
        }
      }
      patch(this.store, { typing: next })
      if (!any && this.typingSweep) {
        clearInterval(this.typingSweep)
        this.typingSweep = null
      }
    }, 1000)
  }

  private wire(sock: AppSocket): void {
    if (this.wired === sock) return
    this.wired?.off('chat:message', this.onMessage)
    this.wired?.off('chat:cleared', this.onCleared)
    this.wired?.off('chat:typing', this.onTyping)
    this.wired?.off('chat:reaction', this.onReaction)
    this.wired = sock
    sock.on('chat:message', this.onMessage)
    sock.on('chat:cleared', this.onCleared)
    sock.on('chat:typing', this.onTyping)
    sock.on('chat:reaction', this.onReaction)
  }

  // ----------------------------------------------------------------- sync

  private history(payload: {
    before?: number
    after?: number
    limit?: number
  }): Promise<AckResult<ChatHistory>> {
    return new Promise((resolve) => {
      const sock = this.socket
      if (!sock) return resolve(offline())
      sock.emit('chat:history', payload, resolve)
    })
  }

  /**
   * On join and after every reconnect. A new session (a different meeting
   * occurrence) starts the chat over; the same one is caught up from the
   * highest seq held, page after page until nothing is missing. Then any of
   * our sends still pending goes again — the server knows the id.
   */
  async sync(): Promise<void> {
    const room = this.room.state.get()
    if (room.phase !== 'joined') return
    if (room.sessionId && room.sessionId !== this.s.sessionId) {
      patch(this.store, {
        messages: [],
        pending: [],
        unread: 0,
        hasOlder: false,
        sessionId: room.sessionId,
      })
    }
    patch(this.store, { loading: true })
    try {
      if (this.lastSeq.get() === 0) {
        const result = await this.history({ limit: PAGE })
        if (!result.ok) return
        patch(this.store, { sessionId: result.data.sessionId })
        for (const m of result.data.messages) this.upsert(m)
        patch(this.store, { hasOlder: result.data.hasMore })
      } else {
        // Catching up: everything after what we have, oldest first, until done.
        for (let page = 0; page < 20; page++) {
          const result = await this.history({ after: this.lastSeq.get(), limit: 100 })
          if (!result.ok) return
          let added = 0
          for (const m of result.data.messages) {
            if (this.upsert(m)) {
              added++
              this.countUnread(m)
            }
          }
          if (!result.data.hasMore || added === 0) break
        }
      }
    } finally {
      patch(this.store, { loading: false })
    }
    for (const line of this.s.pending) {
      if (!line.failed) void this.transmit(line)
    }
  }

  async loadOlder(): Promise<void> {
    const first = this.s.messages[0]
    if (!first || this.s.loading) return
    patch(this.store, { loading: true })
    try {
      const result = await this.history({ before: first.seq, limit: PAGE })
      if (!result.ok) return
      for (const m of result.data.messages) this.upsert(m)
      patch(this.store, { hasOlder: result.data.hasMore })
    } finally {
      patch(this.store, { loading: false })
    }
  }

  // ---------------------------------------------------------------- sending

  private setPending(clientMsgId: string, change: (line: ChatLine) => ChatLine): void {
    patch(this.store, {
      pending: this.s.pending.map((p) => (p.clientMsgId === clientMsgId ? change(p) : p)),
    })
  }

  private transmit(line: ChatLine): Promise<void> {
    return new Promise((resolve) => {
      const sock = this.socket
      const clientMsgId = line.clientMsgId
      if (!sock || !clientMsgId) return resolve()
      sock.emit(
        'chat:send',
        {
          clientMsgId,
          body: line.body,
          ...(line.toPeerId ? { to: line.toPeerId } : {}),
          ...(line.mentions.length > 0 ? { mentions: line.mentions } : {}),
          ...(line.attachments.length > 0
            ? { attachments: line.attachments.map((a) => a.id) }
            : {}),
        },
        (result) => {
          if (!result.ok) {
            this.setPending(clientMsgId, (p) => ({ ...p, failed: result.error.message }))
          }
          // On ok the echo carries the line; nothing to do here.
          resolve()
        },
      )
    })
  }

  /**
   * Render at once, send, and let the echo make it real. `to` makes it a
   * private line; `mentions` is who the composer believes was @-named — the
   * server decides, and the echo carries its answer.
   */
  send(body: string, opts: { to?: PeerId | null; mentions?: PeerId[] } = {}): void {
    const trimmed = body.trim()
    const files = this.readyUploads.get()
    const room = this.room.state.get()
    const me = room.me
    if ((!trimmed && files.length === 0) || !me) return
    const attachments = files.flatMap((u) => (u.attachment ? [u.attachment] : []))
    const line: ChatLine = {
      id: `pending:${newClientMsgId()}`,
      roomId: room.room.roomId,
      sessionId: this.s.sessionId ?? '',
      seq: 0,
      authorPeerId: me,
      authorId: null,
      authorName: room.room.participants[me]?.displayName ?? 'You',
      body: trimmed,
      kind: 'text',
      toPeerId: opts.to ?? null,
      mentions: opts.mentions ?? [],
      createdAt: Date.now(),
      clientMsgId: newClientMsgId(),
      attachments,
      pending: true,
    }
    // The files leave the composer with the line; their previews stay for
    // the optimistic render and a while after the echo, then go.
    const previews = { ...this.s.previews }
    for (const u of files) if (u.preview && u.attachment) previews[u.attachment.id] = u.preview
    patch(this.store, {
      pending: [...this.s.pending, line],
      previews,
      uploads: this.s.uploads.filter((u) => u.status !== 'ready'),
    })
    this.stoppedTyping()
    void this.transmit(line)
  }

  retry(clientMsgId: string): void {
    const line = this.s.pending.find((p) => p.clientMsgId === clientMsgId)
    if (!line) return
    const { failed: _failed, ...again } = line
    this.setPending(clientMsgId, () => again)
    void this.transmit(again)
  }

  discard(clientMsgId: string): void {
    patch(this.store, { pending: this.s.pending.filter((p) => p.clientMsgId !== clientMsgId) })
  }

  /** Write to one participant privately, or to the room with null. */
  setDmTarget(peerId: PeerId | null): void {
    patch(this.store, { dmTarget: peerId })
  }

  /**
   * Called on every keystroke; tells the room — or, for a private line,
   * only its recipient — at most every few seconds.
   */
  typed(): void {
    const now = Date.now()
    const to = this.s.dmTarget
    if (this.typingAnnounced && to !== this.typingTo) this.stoppedTyping()
    if (now - this.lastTypingSentAt < TYPING_SIGNAL_MS) return
    this.lastTypingSentAt = now
    this.typingAnnounced = true
    this.typingTo = to
    this.socket?.emit('chat:typing', { typing: true, ...(to ? { to } : {}) })
  }

  stoppedTyping(): void {
    if (!this.typingAnnounced) return
    this.typingAnnounced = false
    this.lastTypingSentAt = 0
    const to = this.typingTo
    this.socket?.emit('chat:typing', { typing: false, ...(to ? { to } : {}) })
    this.typingTo = null
  }

  /** Up it goes at once on our own stage; the room hears about it a moment later. */
  react(emoji: Reaction): void {
    const me = this.me
    if (me) this.show(me, emoji)
    this.socket?.emit('chat:reaction', { emoji }, () => undefined)
  }

  /** The animated picture of an emoji, from this client's emoji host. */
  emojiUrl(emoji: string): string {
    return animatedEmojiUrl(emoji, this.emojiBase)
  }

  /** Fetch the reaction bar's pictures ahead of the first tap, in the chosen skin tone. */
  preloadReactions(tone: SkinTone = this.s.skinTone): void {
    preloadReactions(tone, this.emojiBase)
  }

  setSkinTone(tone: SkinTone): void {
    patch(this.store, { skinTone: tone })
    writeKey(this.local, SKIN_TONE_KEY, tone || null)
  }

  /** Moderators. The server answers with chat:cleared for everyone, us included. */
  clear(): Promise<AckResult<void>> {
    return this.request((sock, done) => sock.emit('chat:clear', done))
  }

  setEnabled(enabled: boolean): Promise<AckResult<void>> {
    return this.request((sock, done) => sock.emit('request:set-chat-enabled', { enabled }, done))
  }

  // ------------------------------------------------------------ attachments

  private request<T>(
    emitter: (sock: AppSocket, done: (r: AckResult<T>) => void) => void,
  ): Promise<AckResult<T>> {
    return new Promise((resolve) => {
      const sock = this.socket
      if (!sock) return resolve(offline())
      emitter(sock, resolve)
    })
  }

  private patchUpload(localId: string, change: Partial<Upload>): void {
    patch(this.store, {
      uploads: this.s.uploads.map((u) => (u.localId === localId ? { ...u, ...change } : u)),
    })
  }

  /**
   * Add files to the composer and start them on their way: an address from
   * the server, the bytes straight to storage with a progress bar, then the
   * server's look at what landed. Each file is independent; one failing
   * leaves the others where they are, with a reason on the failed one.
   */
  attach(files: Iterable<File>): void {
    for (const file of files) {
      const localId = newClientMsgId()
      const live = this.s.uploads.filter((u) => u.status !== 'failed').length
      const upload: Upload = {
        localId,
        id: null,
        name: file.name || 'file',
        size: file.size,
        status: 'starting',
        progress: 0,
        ...(file.type.startsWith('image/') ? { preview: URL.createObjectURL(file) } : {}),
        file,
      }
      if (live >= ATTACHMENTS_PER_MESSAGE) {
        upload.status = 'failed'
        upload.error = `Up to ${ATTACHMENTS_PER_MESSAGE} files per message.`
      } else if (file.size > ATTACHMENT_MAX_BYTES_DEFAULT) {
        upload.status = 'failed'
        upload.error = `Files up to ${formatBytes(ATTACHMENT_MAX_BYTES_DEFAULT)} can be shared.`
      } else if (file.size === 0) {
        upload.status = 'failed'
        upload.error = 'This file is empty.'
      }
      patch(this.store, { uploads: [...this.s.uploads, upload] })
      if (upload.status !== 'failed') void this.run(localId, file)
    }
  }

  private async run(localId: string, file: File): Promise<void> {
    const begun = await this.request<AttachmentUpload>((sock, done) =>
      sock.emit(
        'chat:attachment-begin',
        { name: file.name || 'file', size: file.size, ...(file.type ? { type: file.type } : {}) },
        done,
      ),
    )
    if (!begun.ok)
      return this.patchUpload(localId, { status: 'failed', error: begun.error.message })
    const abort = new AbortController()
    this.patchUpload(localId, { id: begun.data.id, status: 'uploading', abort })
    try {
      await putWithProgress({
        url: begun.data.url,
        headers: begun.data.headers,
        body: file,
        signal: abort.signal,
        onProgress: (fraction) => this.patchUpload(localId, { progress: fraction }),
      })
    } catch (err) {
      if (abort.signal.aborted) return // removed by the user; the server was told
      void this.request((sock, done) =>
        sock.emit('chat:attachment-abort', { id: begun.data.id }, done),
      )
      return this.patchUpload(localId, {
        status: 'failed',
        error: err instanceof Error ? err.message : 'The upload failed.',
      })
    }
    this.patchUpload(localId, { status: 'finishing', progress: 1 })
    const finished = await this.request<Attachment>((sock, done) =>
      sock.emit('chat:attachment-finish', { id: begun.data.id }, done),
    )
    if (!finished.ok) {
      return this.patchUpload(localId, { status: 'failed', error: finished.error.message })
    }
    this.patchUpload(localId, {
      status: 'ready',
      attachment: finished.data,
      abort: undefined,
      file: undefined,
    })
  }

  /** Take a file out of the composer: stop its upload if it is still going, and tell the server. */
  removeUpload(localId: string): void {
    const upload = this.s.uploads.find((u) => u.localId === localId)
    if (!upload) return
    upload.abort?.abort()
    const id = upload.id
    if (id && upload.status !== 'failed') {
      void this.request((sock, done) => sock.emit('chat:attachment-abort', { id }, done))
    }
    if (upload.preview) URL.revokeObjectURL(upload.preview)
    patch(this.store, { uploads: this.s.uploads.filter((u) => u.localId !== localId) })
  }

  /** Try a failed one again from the start. */
  retryUpload(localId: string): void {
    const upload = this.s.uploads.find((u) => u.localId === localId)
    if (!upload?.file) return
    const file = upload.file
    this.removeUpload(localId)
    this.attach([file])
  }

  /** The local preview of one of our own pictures, while there is one. */
  previewFor(attachmentId: string): string | null {
    return this.s.previews[attachmentId] ?? null
  }

  private dropPreview(attachmentId: string): void {
    const url = this.s.previews[attachmentId]
    if (!url) return
    URL.revokeObjectURL(url)
    const next = { ...this.s.previews }
    delete next[attachmentId]
    patch(this.store, { previews: next })
  }

  /** True while the link held for this file is good for a while yet. */
  linkFresh(attachmentId: string): boolean {
    const link = this.links.get(attachmentId)
    return Boolean(link && link.expiresAt - Date.now() > LINK_MARGIN_MS)
  }

  /**
   * A download link for a file on a line we may read. Cached until it is
   * about to expire; one request in flight per file however many cards
   * ask. Null when the server says no — a private line we are not party
   * to, or a file that is gone.
   */
  urlFor(attachmentId: string, force = false): Promise<string | null> {
    const held = this.links.get(attachmentId)
    if (!force && held && this.linkFresh(attachmentId)) return Promise.resolve(held.url)
    let inflight = this.linkRequests.get(attachmentId)
    if (!inflight) {
      inflight = this.request<AttachmentLink>((sock, done) =>
        sock.emit('chat:attachment-url', { id: attachmentId }, done),
      )
        .then((result) => {
          if (!result.ok) return null
          this.links.set(attachmentId, result.data)
          return result.data.url
        })
        .finally(() => this.linkRequests.delete(attachmentId))
      this.linkRequests.set(attachmentId, inflight)
    }
    return inflight
  }

  // -------------------------------------------------------------- the panel

  private markRead(): void {
    patch(this.store, { unread: 0, unreadMentions: 0 })
  }

  toggle(): void {
    patch(this.store, { open: !this.s.open })
    if (this.s.open && this.s.atBottom) this.markRead()
  }

  setAtBottom(value: boolean): void {
    patch(this.store, { atBottom: value })
    if (value && this.s.open) this.markRead()
  }

  private reset(): void {
    for (const u of this.s.uploads) {
      u.abort?.abort()
      if (u.preview) URL.revokeObjectURL(u.preview)
    }
    for (const url of Object.values(this.s.previews)) URL.revokeObjectURL(url)
    this.links.clear()
    for (const timer of this.tileTimers.values()) clearTimeout(timer)
    this.tileTimers.clear()
    this.typingTo = null
    this.typingAnnounced = false
    this.lastTypingSentAt = 0
    if (this.typingSweep) {
      clearInterval(this.typingSweep)
      this.typingSweep = null
    }
    patch(this.store, {
      messages: [],
      pending: [],
      uploads: [],
      previews: {},
      sessionId: null,
      hasOlder: false,
      unread: 0,
      unreadMentions: 0,
      dmTarget: null,
      typing: {},
      reactions: [],
      tileReactions: {},
    })
  }
}
